
import { Component, Prop, Vue } from 'vue-property-decorator';
import WebsiteRepositoryInterface, {
  Website,
  WebsiteCreation,
  WebsiteCreationAction,
  WebsiteDeletion,
  WebsiteDeletionAction,
  WebsiteStatus,
} from '@/services/Website/interfaces';
import StatusChip from '@/components/layout/StatusChip.vue';
import RecreateWebsiteDialog from '@/components/dialogs/website/RecreateWebsiteDialog.vue';

const HOVER_OPEN_MS = 400;
const HOVER_CLOSE_MS = 220;

const ACTION_LABELS: { [key: string]: string } = {
  create_dns: 'CREATE DNS',
  delete_dns: 'DELETE DNS',
  create_unix_user: 'CREATE UNIX USER',
  delete_unix_user: 'DELETE UNIX USER',
  create_database: 'CREATE DATABASE',
  delete_database: 'DELETE DATABASE',
  create_vhost: 'CREATE VHOST',
  delete_vhost: 'DELETE VHOST',
  create_redis_cache: 'CREATE REDIS CACHE',
  delete_redis_cache: 'DELETE REDIS CACHE',
  create_email: 'CREATE EMAIL',
  delete_email: 'DELETE EMAIL',
  create_letsencrypt: 'CREATE LETS ENCRYPT',
  delete_letsencrypt: 'DELETE LETS ENCRYPT',
  activate_cdn: 'ACTIVATE CDN',
  deactivate_cdn: 'DEACTIVATE CDN',
  install_wp: 'INSTALL WP',
  uninstall_wp: 'UNINSTALL WP',
  clone_wp: 'CLONE WP',
  delete_ftp: 'DELETE FTP',
};

@Component({
  components: {
    StatusChip,
    RecreateWebsiteDialog,
  },
  inject: ['websiteRepository'],
})
export default class WebsiteErrorStatusHover extends Vue {
  @Prop({ type: Object, required: true })
  public website!: Website;

  @Prop({ default: null })
  public creation!: WebsiteCreation | null;

  @Prop({ default: null })
  public deletion!: WebsiteDeletion | null;

  @Prop({ type: Boolean, default: false })
  public orchLoading!: boolean;

  private websiteRepository!: WebsiteRepositoryInterface;

  public menuOpen = false;
  public recreating = false;
  public recreateDialog: string = '';
  public cardStyle: Record<string, string> = {};

  private openTimer: ReturnType<typeof setTimeout> | null = null;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;
  private dialogOpen = false;

  public get isError(): boolean {
    return (this.website.status || '').toUpperCase() === WebsiteStatus.ERROR;
  }

  public get creationActions(): WebsiteCreationAction[] {
    if (!this.creation || !this.creation.actions) {
      return [];
    }
    return this.creation.actions;
  }

  public get deletionActions(): WebsiteDeletionAction[] {
    if (!this.deletion || !this.deletion.actions) {
      return [];
    }
    return this.deletion.actions;
  }

  public onMouseEnter(): void {
    if (!this.isError) {
      return;
    }
    this.clearCloseTimer();
    if (this.menuOpen) {
      return;
    }
    this.clearOpenTimer();
    this.openTimer = setTimeout(() => {
      this.positionCard();
      this.menuOpen = true;
      this.$nextTick(() => this.positionCard());
    }, HOVER_OPEN_MS);
  }

  public onMouseLeave(): void {
    if (this.dialogOpen) {
      return;
    }
    this.clearOpenTimer();
    this.clearCloseTimer();
    this.closeTimer = setTimeout(() => {
      this.menuOpen = false;
    }, HOVER_CLOSE_MS);
  }

  public onCardEnter(): void {
    this.clearCloseTimer();
  }

  public beforeDestroy(): void {
    this.clearOpenTimer();
    this.clearCloseTimer();
  }

  public showRecreateDialog(): void {
    this.dialogOpen = true;
    this.recreateDialog = 'recreate-website-dialog';
  }

  public onRecreateDialogClose(): void {
    this.recreateDialog = '';
    this.dialogOpen = false;
  }

  public async runRecreate(): Promise<void> {
    this.recreating = true;
    this.onRecreateDialogClose();
    try {
      await this.websiteRepository.recreateWebsite(this.website.id);
      this.$successDialog.show('Website Recreation Started');
      this.$set(this.website, 'status', WebsiteStatus.PENDING);
      this.menuOpen = false;
      this.$emit('recreated', this.website.id);
    } catch (err) {
      const anyErr = err as any;
      this.$errorDialog.show(anyErr?.response?.data?.error || anyErr.message);
    } finally {
      this.recreating = false;
    }
  }

  public actionLabel(action: string): string {
    return ACTION_LABELS[action] || (action || 'UNKNOWN').replace(/_/g, ' ').toUpperCase();
  }

  public actionLogs(logs: { log: string }[]): string {
    return (logs || []).map((entry) => entry.log).filter(Boolean).join('\n');
  }

  public statusColor(status: string): string {
    switch ((status || '').toLowerCase()) {
      case 'done':
        return 'success';
      case 'failed':
      case 'cancelled':
        return 'error';
      case 'pending':
      case 'in progress':
      case 'retry':
      case 'retrying':
        return 'info';
      case 'rolling back':
      case 'rolled back':
        return 'orange';
      default:
        return 'grey';
    }
  }

  public statusIcon(status: string): string {
    switch ((status || '').toLowerCase()) {
      case 'done':
        return 'mdi-check';
      case 'failed':
        return 'mdi-close-thick';
      case 'cancelled':
        return 'mdi-cancel';
      case 'pending':
        return 'mdi-progress-clock';
      case 'in progress':
        return 'mdi-progress-helper';
      case 'retry':
      case 'retrying':
        return 'mdi-repeat';
      case 'rolling back':
      case 'rolled back':
        return 'mdi-backup-restore';
      default:
        return 'mdi-circle-outline';
    }
  }

  private positionCard(): void {
    const el = this.$refs.anchor as HTMLElement | undefined;
    if (!el) {
      return;
    }

    const rect = el.getBoundingClientRect();
    const width = Math.min(400, window.innerWidth - 16);
    const maxHeight = Math.round(window.innerHeight * 0.6);
    const left = Math.min(
      Math.max(8, Math.round(rect.left)),
      Math.max(8, window.innerWidth - width - 8),
    );

    const spaceBelow = window.innerHeight - rect.bottom - 12;
    const spaceAbove = rect.top - 12;
    const preferAbove = spaceBelow < 260 && spaceAbove > spaceBelow;

    if (preferAbove) {
      this.cardStyle = {
        top: 'auto',
        bottom: `${Math.round(window.innerHeight - rect.top + 6)}px`,
        left: `${left}px`,
        width: `${width}px`,
        maxHeight: `${Math.min(maxHeight, Math.max(200, spaceAbove))}px`,
      };
      return;
    }

    this.cardStyle = {
      top: `${Math.round(rect.bottom + 6)}px`,
      bottom: 'auto',
      left: `${left}px`,
      width: `${width}px`,
      maxHeight: `${Math.min(maxHeight, Math.max(200, spaceBelow))}px`,
    };
  }

  private clearOpenTimer(): void {
    if (this.openTimer) {
      clearTimeout(this.openTimer);
      this.openTimer = null;
    }
  }

  private clearCloseTimer(): void {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }
}
